import { Badge } from '@/components/ui/elements/badge';
import { Card } from '@/components/ui/elements/card';
import { PolicyGate } from '@/features/auth/authorization';
import { Event } from '@/types';
import { formatDate } from '@/utils/format';
import { formatNumber } from '@/utils/numbers-utils';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import {
  CreditCardIcon,
  MapPinIcon,
  PhoneIcon,
  StarIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { MapPinIcon as MapPinSolid } from '@heroicons/react/24/solid';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { isAndroid, isIOS } from 'react-device-detect';

interface ShiftEventCardProps {
  event: Event;
  isNext?: boolean;
  originAddress?: string | null;
}

export const ShiftEventCard = ({ event, isNext = false, originAddress }: ShiftEventCardProps) => {
  const [notesExpanded, setNotesExpanded] = useState(false);
  const navigate = useNavigate();

  const customerPhone = event.phone ?? event.customer?.phone ?? null;
  const hasBalanceDue = event.balance_due > 0;

  const formattedAddress = [event.address, event.city, event.state, event.zipcode]
    .filter(Boolean)
    .join(', ');

  const directionsUrl = buildDirectionsUrl(formattedAddress, originAddress);

  const startTime = formatDate(event.start_at, 'h:mm a');
  const endTime = formatDate(event.end_at, 'h:mm a');

  const now = new Date();
  const isHappening =
    new Date(event.start_at.replace(' ', 'T')) <= now &&
    new Date(event.end_at.replace(' ', 'T')) > now;

  const activityNames = event.activities?.map((a) => a.name).join(', ') ?? '';

  const vipSummary = event.vips
    ?.map((vip) => {
      const name = [vip.first_name, vip.last_name].filter(Boolean).join(' ');
      return vip.age ? `${name} (${vip.age})` : name;
    })
    .filter(Boolean)
    .join(', ');

  return (
    <Card
      className={`overflow-hidden p-0 ${
        isNext ? 'border-primary/40 shadow-md ring-1 ring-primary/20' : 'border-border/60'
      }`}
    >
      {/* Top row: Time block + event info */}
      <div className="flex">
        {/* Time column */}
        <div
          className={`flex flex-col items-center justify-center px-2.5 py-2.5 min-w-[76px] border-r ${
            isNext ? 'bg-primary/10 border-r-primary/20' : 'bg-muted/40 border-r-border/40'
          }`}
        >
          <span
            className={`text-lg font-bold leading-tight ${
              isNext ? 'text-primary' : 'text-foreground'
            }`}
          >
            {startTime}
          </span>
          <span className="text-xs text-muted-foreground leading-tight">to {endTime}</span>
          {isHappening && (
            <Badge variant="green" size="xs" className="mt-1 uppercase tracking-wider font-bold">
              Now
            </Badge>
          )}
          {isNext && !isHappening && (
            <Badge variant="default" size="xs" className="mt-1 uppercase tracking-wider">
              Next
            </Badge>
          )}
        </div>

        {/* Event summary */}
        <button
          className="flex-1 min-w-0 px-2.5 py-2.5 text-left"
          onClick={() => navigate({ to: `/events/event/${event.id}/overview` })}
        >
          <p className="font-bold text-base leading-snug truncate">{event.name || 'No Name'}</p>
          {activityNames && (
            <p className="text-sm text-muted-foreground truncate">{activityNames}</p>
          )}

          {/* Customer */}
          {event.customer && (
            <div className="flex items-center gap-1.5 mt-1.5 text-sm text-foreground/80">
              <UserIcon className="w-4 h-4 shrink-0" />
              <span className="truncate font-medium">
                {event.customer.full_name}
                {event.guest_count ? ` \u00b7 ${event.guest_count} guests` : ''}
              </span>
            </div>
          )}

          {/* VIPs */}
          {vipSummary && (
            <div className="flex items-center gap-1.5 mt-0.5 text-sm text-foreground/80">
              <StarIcon className="w-4 h-4 shrink-0 text-amber-500" />
              <span className="truncate">{vipSummary}</span>
            </div>
          )}

          {hasBalanceDue && (
            <Badge variant="orange" size="sm" className="mt-1.5">
              {formatNumber(event.balance_due, 'currency')} due
            </Badge>
          )}
        </button>
      </div>

      {/* Address — large tappable row for quick directions */}
      <a
        href={directionsUrl}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-2.5 px-3 py-3 bg-muted/25 border-t border-border/40 text-sm text-foreground/70 active:bg-muted/60 transition-colors min-h-[48px]"
      >
        <MapPinIcon className="w-5 h-5 shrink-0 text-muted-foreground" />
        <span className="leading-snug">{formattedAddress}</span>
      </a>

      {/* Special instructions — expandable, hidden by default */}
      {event.special_instructions && (
        <div className="border-t border-border/40">
          <button
            type="button"
            onClick={() => setNotesExpanded(!notesExpanded)}
            className="flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium text-amber-700 dark:text-amber-300 bg-amber-50/60 dark:bg-amber-950/20 active:bg-amber-100/60 transition-colors min-h-[44px]"
          >
            <span>Special Instructions</span>
            <ChevronDownIcon
              className={`w-5 h-5 shrink-0 transition-transform duration-150 ${
                notesExpanded ? 'rotate-180' : ''
              }`}
            />
          </button>
          {notesExpanded && (
            <div className="px-3 py-2.5 text-sm text-amber-800 dark:text-amber-200 bg-amber-50/40 dark:bg-amber-950/10 border-t border-amber-200/40">
              {event.special_instructions}
            </div>
          )}
        </div>
      )}

      {/* Action bar — large tap targets, icon + label, horizontal */}
      <div className="flex border-t border-border/40 divide-x divide-border/40">
        <a
          href={directionsUrl}
          target="_blank"
          rel="noreferrer"
          className="flex-1 flex flex-col items-center justify-center gap-0.5 py-3 font-medium text-foreground/70 active:bg-muted/60 transition-colors"
        >
          <MapPinSolid className="w-6 h-6 text-primary" />
          <span className="text-xs">Directions</span>
        </a>

        {customerPhone && (
          <a
            href={`tel:${customerPhone}`}
            className="flex-1 flex flex-col items-center justify-center gap-0.5 py-3 font-medium text-foreground/70 active:bg-muted/60 transition-colors"
          >
            <PhoneIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <span className="text-xs">Call</span>
          </a>
        )}

        <PolicyGate policy="payments.create">
          <Link
            to={`/events/event/${event.id}/payments`}
            className="flex-1 flex flex-col items-center justify-center gap-0.5 py-3 font-medium text-foreground/70 active:bg-muted/60 transition-colors"
          >
            <CreditCardIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
            <span className="text-xs">Payment</span>
          </Link>
        </PolicyGate>
      </div>
    </Card>
  );
};

/**
 * Builds a platform-appropriate directions URL.
 * - iOS: Apple Maps (opens native Maps app)
 * - Android: Google Maps intent (opens native Google Maps app)
 * - Desktop: Google Maps web fallback
 */
function buildDirectionsUrl(destination: string, origin?: string | null): string {
  const dest = encodeURIComponent(destination);
  const orig = origin ? encodeURIComponent(origin) : null;

  if (isIOS) {
    // Apple Maps URL scheme — opens natively, supports turn-by-turn
    const params = new URLSearchParams({ daddr: destination, dirflg: 'd' });
    if (origin) params.set('saddr', origin);
    return `maps://?${params.toString()}`;
  }

  if (isAndroid) {
    // Google Maps Android intent — opens native app directly into navigation
    return orig
      ? `https://www.google.com/maps/dir/?api=1&origin=${orig}&destination=${dest}&travelmode=driving`
      : `google.navigation:q=${dest}&mode=d`;
  }

  // Desktop fallback — Google Maps web
  return orig
    ? `https://www.google.com/maps/dir/?api=1&origin=${orig}&destination=${dest}`
    : `https://www.google.com/maps/dir/?api=1&destination=${dest}`;
}
